import type { SerializedFile } from "@repo/models";

import { useVerbiageQuery } from "@repo/baseCloud";
import { PushProcessingNotification } from "@repo/helpers";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	ButtonTypes,
	FileUploader,
} from "@repo/ui";
import { useState } from "react";

import LocationsApi from "../../../api/locationsApi";

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}

export function LocationsImportModal({ open, onOpenChange, onSuccess }: Props) {
	const [files, setFiles] = useState<SerializedFile[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const { verbiage } = useVerbiageQuery();

	const handleSubmit = async (e?: React.FormEvent) => {
		e?.preventDefault();
		if (files.length === 0) return;

		setSubmitting(true);
		const notify = PushProcessingNotification(`Processing ${verbiage?.location} File Import...`);

		try {
			const formData = new FormData();
			const file = files[0];
			if (file.url) {
				const res = await fetch(file.url);
				const blob = await res.blob();
				formData.append("locationsFile", blob, file.name);
			}

			const result = await LocationsApi.saveLocationsFile(formData);
			if (result?.Type !== "Upload") {
				notify.error(`Failed to process ${verbiage?.location} file.`);
				return;
			}
			notify.success(`${verbiage?.location} file processed successfully.`);
			onSuccess?.();
			handleOpenChange(false);
		} catch (error) {
			console.error(error);
			notify.error(`Failed to process ${verbiage?.location} file.`);
		} finally {
			setSubmitting(false);
		}
	};

	const resetForm = () => {
		setFiles([]);
	};

	const handleOpenChange = (isOpen: boolean) => {
		if (!isOpen) {
			resetForm();
		}
		onOpenChange(isOpen);
	};

	const renderBody = () => (
		<div className="flex flex-col gap-4 p-4">
			<FileUploader
				id="locationsFile"
				label={`Select ${verbiage?.location} File`}
				onFilesAdded={(_, newFiles) => setFiles(newFiles)}
				files={files}
				// ReliantLocationsImporter reads Excel only.
				acceptFileTypes={[".xlsx", ".xls"]}
				multiple={false}
				autoReplace={true}
			/>
		</div>
	);

	return (
		<AlertDialog open={open} onOpenChange={handleOpenChange}>
			<AlertDialogContent style={{ maxWidth: "500px" }}>
				<AlertDialogHeader>
					<AlertDialogTitle>Upload {verbiage?.location} File</AlertDialogTitle>
				</AlertDialogHeader>

				<AlertDialogDescription asChild>{renderBody()}</AlertDialogDescription>

				<AlertDialogFooter>
					<AlertDialogAction
						onClick={handleSubmit}
						types={[ButtonTypes.Primary]}
						disabled={submitting || files.length === 0}
					>
						{submitting ? "Submitting..." : "Submit"}
					</AlertDialogAction>
					<AlertDialogCancel onClick={() => handleOpenChange(false)} types={[ButtonTypes.Outline]}>
						Cancel
					</AlertDialogCancel>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
